import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardActionArea,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  Alert,
} from '@mui/material';
import { getMyExperts } from '@services/expert.service';
import { ExpertListItemDto } from '@/types/expert';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { PageHint } from '../../common/PageHint';
import { UserAvatar } from '../../common/UserAvatar';

/** Поріг переключення на картковий вибір. При ≤ цього — UI карток замість Autocomplete. */
const CARD_PICKER_THRESHOLD = 5;

interface ExpertPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (expert: ExpertListItemDto) => void;
}

export const ExpertPickerModal: React.FC<ExpertPickerModalProps> = ({ open, onClose, onSelect }) => {
  const [experts, setExperts] = useState<ExpertListItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ExpertListItemDto | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setError(null);
    setLoading(true);
    getMyExperts('active')
      .then(res => setExperts(res.data))
      .catch(() => setError('Не вдалося завантажити список експертів'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleConfirm = () => {
    if (selected) onSelect(selected);
  };

  const useCards = !loading && experts.length > 0 && experts.length <= CARD_PICKER_THRESHOLD;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" data-i-doc={ModalIndices.EXPERT_PICKER}>
      <DialogTitle>Нова зустріч</DialogTitle>
      <PageHint iDoc={ModalIndices.EXPERT_PICKER} />
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && experts.length === 0 && !error && (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            У вас поки немає активних експертів. Знайдіть експерта у каталозі і подайте заявку на співпрацю.
          </Typography>
        )}

        {useCards && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {experts.map(e => (
              <Card key={e.id} variant="outlined">
                <CardActionArea onClick={() => onSelect(e)} sx={{ p: 1.5, display: 'flex', gap: 1.5, justifyContent: 'flex-start' }}>
                  <UserAvatar name={e.name} path={e.avatar} sx={{ width: 40, height: 40 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                      {e.name}
                    </Typography>
                    {e.specializationsString && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {e.specializationsString}
                      </Typography>
                    )}
                  </Box>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}

        {!loading && experts.length > CARD_PICKER_THRESHOLD && (
          <Autocomplete
            sx={{ mt: 1 }}
            options={experts}
            value={selected}
            onChange={(_, value) => setSelected(value)}
            getOptionLabel={opt => opt.name}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderInput={params => (
              <TextField {...params} label="Оберіть експерта" />
            )}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        {/* Кнопка "Далі" потрібна лише для Autocomplete-режиму; для карток клік сам викликає onSelect. */}
        {!useCards && experts.length > CARD_PICKER_THRESHOLD && (
          <Button onClick={handleConfirm} variant="contained" disabled={!selected}>
            Далі
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
